import React, { useState, useMemo } from "react";
import NavBarEN from "./NavBarEN";
import NavBarMobileEN from "./NavBarMobileEN";
import { useMediaQuery } from "@uidotdev/usehooks";
import FormEN from "./FormEN";
import FooterEN from "./FooterEN";

import imageSlika1 from "../catalog/ledena-kisa-slika1-uvod-en.jpg";
import imageSlika2 from "../catalog/ledena-kisa-slika2-klimatologija.jpg";
import imageSlika3 from "../catalog/ledena-kisa-slika3-trend-1949-1985-en.jpg";
import imageSlika4 from "../catalog/ledena-kisa-slika4-trend-1991-2022-en.jpg";
import imageSlika5 from "../catalog/ledena-kisa-slika5-trajanje-en.jpg";
import imageSlika6 from "../catalog/ledena-kisa-slika6-pocetak-kraj-en.jpg";
import imageSlika7 from "../catalog/ledena-kisa-slika7-profili-en.jpg";
import imageSlika8 from "../catalog/ledena-kisa-slika8-ruze-vetra.jpg";

import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from "@material-tailwind/react";
import ReactHtmlParser from "html-react-parser";

/*
  Isti folderi kao u srpskoj verziji (src/):
  - src/slikeLedenaKisaGodine/
  - src/slikeLedenaKisaSondaze/
*/

const figure = (src, caption, alt = "Freezing rain") =>
  `<figure style="margin-bottom: 16px;">
     <img src="${src}" alt="${alt}" style="max-width:100%;height:auto;margin:0 auto;">
     <figcaption style="color: #000; font-size: 14px; text-align: left; margin-top: 6px;">${caption}</figcaption>
   </figure>`;

const MESECI = {
  jan: "01",
  feb: "02",
  mar: "03",
  apr: "04",
  may: "05",
  jun: "06",
  jul: "07",
  aug: "08",
  sep: "09",
  oct: "10",
  nov: "11",
  dec: "12",
};

export default function LedeneKiseEN() {
  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");
  const isTabletOrLarger = useMediaQuery("only screen and (min-width: 769px)");
  const [activeTab, setActiveTab] = useState("introduction");
  const [activePodaciTab, setActivePodaciTab] = useState("days-per-year");

  const imagesGodine = useMemo(
    () =>
      require.context("../slikeLedenaKisaGodine", false, /\.(png|jpe?g|svg)$/),
    [],
  );
  const imagesSondaze = useMemo(
    () =>
      require.context("../slikeLedenaKisaSondaze", false, /\.(png|jpe?g|svg)$/),
    [],
  );

  const godine = useMemo(
    () =>
      imagesGodine
        .keys()
        .map((key) => {
          const match = key.match(/(\d{4})/);
          return match ? { key, label: match[1] } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [imagesGodine],
  );

  const sondaze = useMemo(
    () =>
      imagesSondaze
        .keys()
        .map((key) => {
          const match = key.match(/(\d{1,2})([a-z]{3})(\d{4})/i);
          if (!match) return null;
          const d = match[1].padStart(2, "0");
          const m = MESECI[match[2].toLowerCase()];
          const y = match[3];
          if (!m) return null;
          return { key, label: `${d}.${m}.${y}`, sortKey: `${y}${m}${d}` };
        })
        .filter(Boolean)
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey)),
    [imagesSondaze],
  );

  const [godina, setGodina] = useState("");
  const [sondaza, setSondaza] = useState("");

  const selectedGodinaKey = godina || (godine[0] && godine[0].key) || "";
  const selectedSondazaKey = sondaza || (sondaze[0] && sondaze[0].key) || "";

  const godinaLabel =
    (godine.find((g) => g.key === selectedGodinaKey) || {}).label || "";
  const sondazaLabel =
    (sondaze.find((s) => s.key === selectedSondazaKey) || {}).label || "";

  const godinaImage = selectedGodinaKey ? imagesGodine(selectedGodinaKey) : "";
  const sondazaImage = selectedSondazaKey
    ? imagesSondaze(selectedSondazaKey)
    : "";

  const data = [
    {
      label: "INTRODUCTION",
      value: "introduction",
      desc:
        `<h2>INTRODUCTION</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Freezing precipitation is typically categorised into three types: freezing rain, freezing drizzle, and freezing fog. This phenomenon occurs when raindrops or drizzle particles freeze instantly on contact with surface objects such as asphalt, concrete, power lines or tree branches that have a temperature below 0 °C, resulting in the formation of a thin layer of ice (glaze) on their surfaces. Freezing rain occurs when falling ice particles (snowflakes, sleet and/or ice pellets) hit a warm layer of air, the so-called “warm nose”, which lies above a superficial layer of cold air. This layer of warm air is deep enough to melt the ice particles and turn them into liquid droplets. These droplets then fall into the cold layer and become supercooled droplets, which immediately freeze on impact with cold surfaces (Figure 1). The occurrence of freezing precipitation, as an extreme and severe weather phenomenon, is usually associated with a temperature inversion in the lowest layer of the troposphere and the presence of a warm air layer above the ground, although there have also been episodes of freezing rain without a “warm nose” above the ground. It is often associated with extratropical cyclones, nearby water surfaces, and terrain.
      </p>` +
        `<p style="color: #000;">
        This type of extreme weather, although rare, is one of the most dangerous phenomena. Freezing rain can lead to natural disasters in various regions of the world and pose a risk to people, transportation and property. The occurrence of freezing rain can lead to the accumulation of ice on trees or man-made objects, causing hazardous traffic conditions, toppling power lines and transmission towers, damage to infrastructure and injuries to people on flat, paved surfaces. The knowledge gained from freezing precipitation research can provide strategies to reduce risk and improve the resilience of communities affected by these events. This can lead to the development of disaster management strategies and improve the safety and well-being of affected communities.
      </p>`,
      image: figure(
        imageSlika1,
        "<b>Figure 1</b> Schematic representation of a typical vertical temperature profile (red line) during an episode of freezing rain at the ground.",
        "Freezing rain - introduction",
      ),
      desc2: `<p></p>`,
    },
    {
      label: "CLIMATOLOGY",
      value: "climatology",
      desc:
        `<h2>CLIMATOLOGY OF FREEZING RAIN IN SERBIA</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Figure 2 shows the distribution of the average annual number of days with freezing rain across the territory of Serbia during the most recent climatological reference period (1991–2020), based on standard observations from the meteorological network of 28 main meteorological stations in Serbia. It can be seen that eastern Serbia has the highest annual number of these days (the maximum is approximately 5.4 days per year for the station “Crni vrh”). This is the area of the Carpathian Mountains in Serbia. Furthermore, the study (Kämäräinen et al. 2017) shows that the area near the Carpathians has the highest frequency of freezing rain in Europe. In Belgrade, on the other hand, the average annual number of days with freezing rain during the specified standard climatological period is slightly lower (3.6 days per year). It should be noted that in most parts of Serbia, less than 2 days of freezing rain per year were recorded in the last thirty-year climatological reference period.
      </p>` +
        `<p style="color: #000;">
        Kämäräinen M, Hyvärinen O, Jylhä K, Vajda A, Neiglick S, Nuottokari J, Gregow H (2017) A method to estimate freezing rain climatology from ERA-Interim reanalysis over Europe. Nat Hazards Earth Syst Sci, 17:243–259.
      </p>`,
      image: figure(
        imageSlika2,
        "<b>Figure 2</b> The spatial distribution of the mean annual number of days with freezing rain over Serbia in the last thirty-year climatological reference period (1991–2020).",
        "Freezing rain - climatology",
      ),
      desc2: `<p></p>`,
    },
    {
      label: "TRENDS AND DURATION",
      value: "trends-and-duration",
      desc:
        `<h2>TRENDS AND DURATION</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Trend analysis of the number of days with freezing rain in Belgrade was carried out for the period 1949–2022. No data were available for the period 1986–1990. The linear regression line for the first period (1949–1985) is a decreasing function with a very low rate of 0.0303 days per year and very low statistical significance (R² = 0.026) (Figure 3). The second period (1991–2022) is characterised by a continuous data series that is longer than the climatological minimum (≥ 30 years). The annual number of days with freezing rain decreased at a rate of 0.1347 days per year in the period 1991–2022 (trendline equation: y = –0.1347 ⸱ x + 273.71; Figure 4). This trend is statistically significant (α = 0.02984). The coefficient of determination (R²) is 0.26. The total number of days per year during this period did not exceed 9. A decrease in the number of days with freezing rain can be observed over time, which can be attributed to global warming.
      </p>` +
        `<p style="margin-bottom: 10px; color: #000;">
        Figure 5 presents the distribution of the duration of freezing rain events in one-hour intervals up to a duration of 10 hours for all freezing rain events (black filled bars) and especially for the DJF season (grey filled bars) for the period 1949–1985. Most freezing rain events are short-lived, with about 40% of all events lasting less than 1 hour and about 62% lasting less than 2 hours. The frequency of the duration of freezing rain decreases rapidly over longer periods of time. Only 1.6% of all freezing rain events lasted 10 hours or longer. In the winter season, freezing rain events have a similar duration to those in all seasons (approximately 39% and 61% lasted up to 1 and up to 2 hours, respectively).
      </p>` +
        `<p style="color: #000;">
        Figure 6 shows the diurnal distributions of the start (black filled bars) and end (grey filled bars) of freezing rain events for the same period 1949–1985. Freezing rain most often begins during the night and early morning, in about 48% of cases, when a temperature inversion and ground-level air temperatures below 0 °C are more likely. At this time of day, there are peaks between 00:00 and 01:00 (15.8%) and between 06:00 and 07:00 (11.1%) local time. A high frequency of the onset of the phenomenon is also observed during the afternoon, probably due to the passage of warm fronts or on icy days. The end of freezing rain events is approximately evenly distributed throughout the day, with a maximum between 04:00 and 05:00 local time (about 11%) and several other peaks during the day.
      </p>`,
      image:
        `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px;">` +
        figure(
          imageSlika3,
          "<b>Figure 3</b> The total number of days with freezing rain per year in Belgrade for the period 1949–1985. The bold red line represents the linear regression.",
          "Figure 3",
        ) +
        figure(
          imageSlika4,
          "<b>Figure 4</b> Same as Figure 3, but for the period 1991–2022.",
          "Figure 4",
        ) +
        figure(
          imageSlika5,
          "<b>Figure 5</b> The duration (in hours) of freezing rain events (black filled bars) and especially for the winter season (grey filled bars) for the period 1949–1985.",
          "Figure 5",
        ) +
        figure(
          imageSlika6,
          "<b>Figure 6</b> The frequency of the start (black filled bars) and end (grey filled bars) of freezing rain events (UTC+1) per hour for the period 1949–1985.",
          "Figure 6",
        ) +
        `</div>`,
      desc2: `<p></p>`,
    },
    {
      label: "VERTICAL STRUCTURE OF THE ATMOSPHERE",
      value: "vertical-structure",
      desc:
        `<h2>VERTICAL STRUCTURE OF THE ATMOSPHERE</h2>
      <p style="margin-bottom: 10px; color: #000;">
        Figure 7 shows vertical profiles of air temperature in the lowest 2 km, obtained from radiosonde measurements in Belgrade on days with freezing rain at 00 UTC, for the period 1974–2022. Characteristic vertical profiles of air temperature during freezing rain usually include a temperature inversion in the lowest layer immediately above the ground, as well as the appearance of the so-called “warm nose”, characterised by temperatures above 0 °C near 850 hPa (about 1.5 km altitude) and decreasing temperatures above. The mean soundings for November and December show a characteristic vertical temperature profile with a “warm nose” above the supercooled surface air layer (t &lt; 0 °C). Mean soundings for the other months do not show this characteristic vertical temperature profile, which is a consequence of averaging a larger number of soundings without a “warm nose”. They also have relatively weak supercooled surface layers (down to –3 °C), except in February. All vertical profiles show one or more temperature inversions. When a “warm nose” is present, solid precipitation particles (snowflakes, ice grains and sleet) fall through this relatively warm layer of air and melt; as they continue to fall towards the ground they become supercooled drops that instantly freeze on contact with objects, forming a thin layer of ice on their surfaces. Out of a total of 96 analysed soundings, 58 (60.4%) show a vertical profile with a “warm nose”. This is in agreement with a study (Carrière et al. 2000), which points out that 30–40% of soundings do not have this characteristic at altitude.
      </p>` +
        `<p style="color: #000;">
        Carrière, J.-M., Lainard, C., Le Bot, C., Robart, F. 2000. A climatological study of surface freezing precipitation in Europe, Meteorol. Appl., 7:229–238.
      </p>`,
      image: figure(
        imageSlika7,
        "<b>Figure 7</b> Mean vertical profiles of air temperature by month for the station “Belgrade-Košutnjak” at 00 UTC during freezing rain events in the period 1974–2022.",
        "Vertical structure of the atmosphere",
      ),
      desc2: `<p></p>`,
    },
    {
      label: "FREEZING RAIN AND KOSAVA WIND",
      value: "freezing-rain-and-kosava",
      desc:
        `<h2>FREEZING RAIN AND KOSAVA WIND</h2>
      <p style="margin-bottom: 10px; color: #000;">
        A strong link between the occurrence of freezing rain and the Kosava wind (Kovačević et al., 2025) has been confirmed by analysing mean synoptic charts at ground level and at altitude for days when this extreme weather event occurred (not shown). Freezing rain often occurs when blocking anticyclones develop east of Serbia and cyclones form over the Apennine Peninsula and the Western Mediterranean. These are typical synoptic situations in which Kosava also occurs, a strong local wind from the east and southeast along approximately parallel and almost meridional isobars over Serbia. Figure 8 shows this connection, based on wind roses by speed (a, b) and frequency (c, d) for the station “Belgrade-Vračar” for 07:00 (a, c) and 21:00 (b, d) local time. These two climatological terms were selected because freezing rain most often occurs during the night and early morning, and because Kosava usually reaches its maximum between 05:00 and 10:00 local time. It can be seen that southeast (SE), east-southeast (ESE) and south-southeast (SSE) winds prevail on days when freezing rain was recorded in Belgrade. The total share of winds from these directions is about 43% at 07:00 and about 46% at 21:00. In addition, ESE, SE and SSE winds have higher speeds (a, b), indicating Kosava, a wind that typically blows from the southeast and is characterised by higher speed.
      </p>` +
        `<p style="color: #000;">
        Kovačević, N., Koračin, K.V., Putniković, S. 2025. Climatological study of freezing rain in Belgrade from 1949 to 2022, Theor. Appl. Climatol., 156, 200.
      </p>`,
      image: figure(
        imageSlika8,
        "<b>Figure 8</b> Wind roses for (a, b) wind speed (in m s<sup>–1</sup>) and (c, d) wind frequency (in percent) for 07:00 local time (a, c) and 21:00 local time (b, d) on days with freezing rain in Belgrade.",
        "Freezing rain and Kosava wind",
      ),
      desc2: `<p></p>`,
    },
    {
      label: "DATA",
      value: "data",
      tabs: [
        {
          label: "Number of days per year",
          value: "days-per-year",
          content: (
            <div
              className={`suseDeo ${
                isTabletOrLarger ? "flex" : "flex flex-col"
              }`}
            >
              <div className="suseDeoVeci w-full md:w-2/3 p-4">
                <p className="mb-4">
                  Select a year within the latest climatological reference
                  period (1991–2020).
                </p>

                <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-4">
                  <label className="font-semibold">Select a year:</label>
                  <select
                    value={selectedGodinaKey}
                    onChange={(e) => setGodina(e.target.value)}
                    className="border p-2 w-full md:w-auto"
                  >
                    {godine.map(({ key, label }) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {godinaImage && (
                  <div className="mt-6 w-full md:w-auto">
                    <h4 className="text-lg font-semibold">
                      Number of days with freezing rain, {godinaLabel}
                    </h4>
                    <img
                      src={godinaImage}
                      alt={`Number of days with freezing rain, ${godinaLabel}`}
                      className="w-full md:w-auto max-w-md mx-auto"
                    />
                  </div>
                )}

                <div className="mt-6 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                  <a
                    href={godinaImage}
                    download={`Freezing-rain-${godinaLabel}.jpg`}
                    className="bg-blue-500 font-bold text-white px-4 py-2 rounded text-center"
                  >
                    Download image
                  </a>
                </div>
              </div>

              <div className="suseDeoManji w-full md:w-1/3 p-4 bg-white border-l">
                <h4 className="text-lg font-semibold">
                  NUMBER OF DAYS WITH FREEZING RAIN PER YEAR
                </h4>
                <p className="mt-4 text-black">
                  Here you can download figures showing the spatial distribution
                  of the number of freezing rain days per year for the latest
                  climatological reference period (1991–2020).
                </p>
              </div>
            </div>
          ),
        },
        {
          label: "Vertical soundings",
          value: "vertical-soundings",
          content: (
            <div
              className={`suseDeo ${
                isTabletOrLarger ? "flex" : "flex flex-col"
              }`}
            >
              <div className="suseDeoVeci w-full md:w-2/3 p-4">
                <p className="mb-4">
                  Select dates in the period 1974–2020 on which freezing rain
                  occurred in Belgrade to obtain a vertical sounding of the
                  atmosphere.
                </p>

                <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-4">
                  <label className="font-semibold">Select date:</label>
                  <select
                    value={selectedSondazaKey}
                    onChange={(e) => setSondaza(e.target.value)}
                    className="border p-2 w-full md:w-auto"
                  >
                    {sondaze.map(({ key, label }) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {sondazaImage && (
                  <div className="mt-6 w-full md:w-auto">
                    <h4 className="text-lg font-semibold">
                      Vertical sounding of the atmosphere, {sondazaLabel}
                    </h4>
                    <img
                      src={sondazaImage}
                      alt={`Vertical sounding of the atmosphere, ${sondazaLabel}`}
                      className="w-full md:w-auto max-w-md mx-auto"
                    />
                  </div>
                )}

                <div className="mt-6 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
                  <a
                    href={sondazaImage}
                    download={`Sounding-${sondazaLabel}.png`}
                    className="bg-blue-500 font-bold text-white px-4 py-2 rounded text-center"
                  >
                    Download image
                  </a>
                </div>
              </div>

              <div className="suseDeoManji w-full md:w-1/3 p-4 bg-white border-l">
                <h4 className="text-lg font-semibold">
                  VERTICAL SOUNDING OF THE ATMOSPHERE
                </h4>
                <p className="mt-4 text-black">
                  Here you can download vertical soundings of the atmosphere for
                  days with freezing rain in Belgrade from 1974 to 2020.
                </p>
              </div>
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <>
      <style>{`
        .ledenaKisaStranica .suseDeoManji h2 {
          font-size: clamp(1.1rem, 1.4vw, 1.35rem) !important;
          line-height: 1.4 !important;
          font-weight: 700 !important;
          margin: 0 0 0.75rem 0 !important;
          text-align: left !important;
        }
        .ledenaKisaStranica .suseDeoManji p {
          font-size: clamp(0.9rem, 1vw, 1rem) !important;
          line-height: 1.65 !important;
          text-align: justify;
        }
        .ledenaKisaStranica .suseDeo h4 {
          font-size: clamp(0.95rem, 1.1vw, 1.1rem) !important;
          line-height: 1.4 !important;
        }

        @media (min-width: 769px) {
          .ledenaKisaStranica .tabsGlavni {
            gap: 12px !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
          }
          .ledenaKisaStranica .tabsGlavni .probaTaba {
            flex: 1 1 auto !important;
            width: auto !important;
            min-width: 120px;
            padding: 10px 16px !important;
            height: auto !important;
            border: 1px solid #000 !important;
            border-radius: 10px !important;
            background: transparent !important;
          }
          .ledenaKisaStranica .tabsGlavni .probaTaba span {
            font-size: clamp(0.85rem, 1.1vw, 1.1rem) !important;
            font-weight: 700 !important;
            line-height: 1.25 !important;
            color: #000 !important;
          }
        }
        @media (max-width: 768px) {
          .ledenaKisaStranica .suseDeoManji h2 {
            font-size: 1.15rem !important;
          }
          .ledenaKisaStranica .tabsGlavni {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .ledenaKisaStranica .tabsGlavni .probaTaba {
            width: 100% !important;
            margin: 0 !important;
            padding: 10px 12px !important;
            height: auto !important;
            border: 1px solid #000 !important;
            border-radius: 10px !important;
            background: transparent !important;
          }
          .ledenaKisaStranica .tabsGlavni .probaTaba span {
            font-size: 1.25rem !important;
            font-weight: 700 !important;
            color: #000 !important;
          }
        }
      `}</style>

      {isTabletOrLarger && <NavBarEN />}
      {isSmallDevice && <NavBarMobileEN />}
      <div className="ledenaKisaStranica container mx-auto text-center min-h-screen md:mb-6">
        <Tabs value={activeTab} className="max-w-screen-xl mx-auto">
          <TabsHeader
            className="tabsGlavni bg-transparent flex justify-center flex-wrap gap-2 mx-2 md:mx-0"
            indicatorProps={{
              className: "bg-gray-900/10 shadow-none !text-gray-900",
            }}
            onChange={(value) => setActiveTab(value)}
          >
            {data.map(({ label, value }) => (
              <Tab className="probaTaba" key={value} value={value}>
                <span style={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {label}
                </span>
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody>
            {data.map(({ value, tabs, desc, image, desc2 }) => (
              <TabPanel key={value} value={value}>
                {value === "data" ? (
                  <Tabs
                    value={activePodaciTab}
                    onChange={(val) => setActivePodaciTab(val)}
                  >
                    <TabsHeader className="bg-gray-200">
                      {tabs.map(({ label, value }) => (
                        <Tab key={value} value={value}>
                          <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>
                            {label}
                          </span>
                        </Tab>
                      ))}
                    </TabsHeader>
                    <TabsBody>
                      {tabs.map(({ value, content }) => (
                        <TabPanel key={value} value={value}>
                          {content}
                        </TabPanel>
                      ))}
                    </TabsBody>
                  </Tabs>
                ) : (
                  <>
                    <div className="suseDeo flex flex-col md:flex-row">
                      <div className="suseDeoVeci w-full md:w-3/5 p-4">
                        {image && ReactHtmlParser(image)}
                        {desc2 && ReactHtmlParser(desc2)}
                      </div>
                      <div className="suseDeoManji w-full md:w-2/5 p-4 bg-white mt-6 md:mt-0">
                        {desc && ReactHtmlParser(desc)}
                      </div>
                    </div>
                  </>
                )}
              </TabPanel>
            ))}
          </TabsBody>
        </Tabs>
      </div>
      <FormEN />
      <FooterEN />
    </>
  );
}
